import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';

import {
    AcquisitionStrategy,
    ComponentDefinition,
    ConnectionPoint,
    ConnectionPointType,
    Dictionary,
    DigitalSet,
    EditSpotValue,
    Group,
    IApplicationConfiguration,
    IDateRangePickerOptions,
    Identifier,
    IFrequency,
    IValueViewModel,
    RefreshClickData,
    SaveSpotValuesDto,
    SpotValue,
    SpotValuesCount,
    TagDefinition,
    TimeUnit,
    Unit,
    VariableType
} from '../../../models/index';
import { ISpotValuesService, IStoreService, ITranslatedLoggerService } from '../../../services';
import { Store, StoreElement } from '../../../stateManagement';
import { refreshClick } from '../../../common/events';
import { saveErrorMessage } from '../../../common/constants';

interface SpotTagViewModel {
    tag: TagDefinition;
    valuesCount: number;
    totalValuesCount: number;
}

class SpotValuesStateController implements angular.IComponentController {
    actualBeginDate: string;
    actualEndDate: string;
    connectionPoints: Dictionary<ConnectionPoint>;
    digitalSets: Dictionary<DigitalSet>;
    filteredTags: TagDefinition[];
    formController: angular.IFormController;
    frequency: IFrequency;
    groups: Dictionary<Group>;
    identifiers: Dictionary<Identifier>;
    isEditMode: boolean;
    isLoading: boolean;
    selected: SpotTagViewModel;
    spotTags: TagDefinition[];
    tableViewModels: SpotTagViewModel[];
    units: Dictionary<Unit>;
    values: EditSpotValue[];
    valueViewModels: Dictionary<IValueViewModel>;
    variableTypes: Dictionary<VariableType>;

    private _added: EditSpotValue[];
    private _deleted: EditSpotValue[];
    private _updated: EditSpotValue[];
    private _pristineValues: EditSpotValue[];
    private _range: RefreshClickData;
    private _spotValuesCount: SpotValuesCount;

    readonly appConfig: IApplicationConfiguration;
    readonly dateOptions: IDateRangePickerOptions;

    static $inject = [
        '$scope',
        'spotValuesService',
        'storeService',
        'translatedLoggerService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _spotValuesService: ISpotValuesService,
        private _storeService: IStoreService,
        private _loggerService: ITranslatedLoggerService
    ) {
    }

    $onInit() {
        this.frequency = {
            interval: 1,
            intervalTimeUnit: TimeUnit.Minute
        };
        this._storeService.register(
            store => this.refreshStore(store),
            [
                StoreElement.ConnectionPoints,
                StoreElement.DigitalSets,
                StoreElement.Tags,
                StoreElement.Variables
            ]
        );

        this._$scope.$watch(
            () => this.filteredTags,
            () => {
                if (this.filteredTags) {
                    const data: SpotValuesCount = this._spotValuesCount || { totalCount: {}, countBetweenDates: {} };
                    this.tableViewModels = this.filteredTags.map(t => {
                        return {
                            tag: t,
                            valuesCount: data.countBetweenDates[t.guid] ?? 0,
                            totalValuesCount: data.totalCount[t.guid] ?? 0
                        };
                    });
                    this.reselect();
                }
            }
        );

        this._$scope.$on(refreshClick, (event, data: RefreshClickData) => {
            if (data && data.beginDate && data.endDate) {
                this._range = data;
            }
            this.refreshCount(this._range.beginDate, this._range.endDate);
        });
    }

    select(vm: SpotTagViewModel) {
        if (this.formController?.$dirty) {
            this._loggerService.logWarning(saveErrorMessage);
        } else {
            this.selected = vm;
            if (this._range) {
                this.refreshValues(this._range.beginDate, this._range.endDate);
            }
        }
    }

    onValueSaved(value: SpotValue) {
        if (this._range) {
            this.refreshCount(this._range.beginDate, this._range.endDate);
        }
    }

    onValueChanged(value: EditSpotValue) {
        if (this._updated.indexOf(value) < 0
            && this._added.indexOf(value) < 0) {
            this._updated.push(value);
        }
    }

    setEditMode() {
        this.isEditMode = true;
        this._deleted = [];
        this._added = [];
        this._updated = [];
        if (this.values) {
            this._pristineValues = this.values.map(v => Object.assign({}, v));
        }
    }

    addRow() {
        let beginMoment = moment();
        if (this._range && (beginMoment < moment(this._range.beginDate) || beginMoment > moment(this._range.endDate))) {
            beginMoment = moment(this._range.beginDate);
        }
        const newValue: EditSpotValue = {
            comment: null,
            dataTransactionId: null,
            logbookEntryId: null,
            momentTimestamp: beginMoment,
            originalTimestamp: null,
            tagGuid: this.selected.tag.guid,
            timestamp: beginMoment.toISOString()
        };
        if (!!this.values) {
            const values = this.values.map(d => d);
            values.splice(0, 0, newValue);
            this.values = values;
        } else {
            this.values = [newValue];
        }
        this._added = this._added.concat([newValue]);
        if (this.formController) {
            this.formController.$setDirty();
        }
    }

    deleteRow(spotValue: EditSpotValue, $index: number) {
        const values = this.values.map(d => d);
        const spliced = values.splice($index, 1);
        this.values = values;
        const addedIndex = this._added.indexOf(spotValue);
        if (addedIndex >= 0) {
            this._added.splice(addedIndex, 1);
        } else {
            this._deleted = this._deleted.concat(spliced);
        }
        const updatedIndex = this._updated.indexOf(spotValue);
        if (updatedIndex >= 0) {
            this._updated.splice(updatedIndex, 1);
        }
    }

    save() {
        this.isLoading = true;
        try {
            this._added.forEach(d => d.timestamp = d.momentTimestamp.toISOString());
            this._updated
                .forEach(d => d.timestamp = d.momentTimestamp.toISOString());
            const toSave: SaveSpotValuesDto = {
                added: this._added,
                updated: this._updated,
                deleted: this._deleted
            };
            this._spotValuesService
                .save(toSave, true)
                .then(data => {
                    this._loggerService.logSuccess('Spot values successfully saved.');
                    this._added = [];
                    this._deleted = [];
                    this._updated = [];
                    this.isEditMode = false;
                    this.refreshCount(this._range.beginDate, this._range.endDate);
                    this.refreshValues(this._range.beginDate, this._range.endDate);
                })
                .catch(() => this._loggerService.logError('Failed to save spot values.'))
                .finally(() => {
                    this.isLoading = false;
                });
        }
        catch (e) {
            this._loggerService.logError('Failed to save spot values.');
            this.isLoading = false;
        }

    }

    cancel() {
        this.isEditMode = false;
        if (this._pristineValues) {
            this.values = this._pristineValues.map(v => Object.assign({}, v));
        } else {
            this.values = [];
        }
    }

    private refreshCount(beginDate: string, endDate: string) {
        this.isLoading = true;
        this._spotValuesService
            .count(beginDate, endDate)
            .then(data => {
                this._spotValuesCount = data;
                this.tableViewModels = this.filteredTags.map(t => {
                    return {
                        tag: t,
                        valuesCount: data.countBetweenDates[t.guid] ?? 0,
                        totalValuesCount: data.totalCount[t.guid] ?? 0
                    };
                });
                this.reselect();
            })
            .finally(() => this.isLoading = false);
    }

    private refreshValues(beginDate: string, endDate: string) {
        if (this.selected) {
            this.isLoading = true;
            return this._spotValuesService
                .getBetweenDates(this.selected.tag.guid, beginDate, endDate)
                .then(values => this.values = values
                    .sort((a, b) => {
                        if (a.timestamp < b.timestamp) {
                            return -1;
                        } else {
                            return 1;
                        }
                    })
                    .map(d => {
                        const ev = Object.assign({}, d, {
                            originalTimestamp: d.timestamp,
                            momentTimestamp: moment(d.timestamp)
                        });
                        return ev;
                    }))
                .finally(() => this.isLoading = false);
        }
    }

    private refreshStore(store: Store): void {
        const manualConnectionPoints = _.values(store.connectionPoints.dictionary)
            .filter(cp => cp.type === ConnectionPointType.Manual)
            .map(cp => cp.guid);

        const spotTags = _.values(store.tags.dictionary)
            .filter(t =>
                t.acquisitionStrategy === AcquisitionStrategy.Spot
                && manualConnectionPoints.indexOf(t.acquisitionPointGuid) >= 0
            );

        this.spotTags = spotTags;
        this.digitalSets = store.digitalSets.dictionary;
        this.variableTypes = store.variablesTypes.dictionary;
        this.units = store.units.dictionary;
        this.groups = store.groups.dictionary;
        this.connectionPoints = store.connectionPoints.dictionary;
        this.identifiers = store.identifiers.dictionary;
        this.valueViewModels = store.valueViewModels.dictionary;
    }

    // reselect old element or first
    private reselect() {
        if (this.filteredTags?.length > 0) {
            const selected = _.find(this.filteredTags, t => t.guid === this.selected?.tag.guid);
            if (selected) {
                this.select(_.find(this.tableViewModels, vm => vm.tag === selected));
            } else {
                this.select(this.tableViewModels[0]);
            }
        }
    }
}

export const wtSpotValuesState: ComponentDefinition = {
    componentId: 'wtSpotValuesState',
    controller: SpotValuesStateController,
    controllerAs: 'vm',
    template: require('./spotValuesState.component.html'),
    bindings: {

    }
};